import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiClient";
import { FinanceErrorBanner } from "@/lib/financeUi";
import { Plus } from "lucide-react";

type ItemRow = {
  id: string;
  name?: string;
  description?: string | null;
  sku?: string | null;
  unitPrice?: number;
  currency?: string;
  active?: boolean;
};

type ItemsResponse = {
  items?: ItemRow[];
  total?: number;
};

type Draft = { name: string; description: string; unitPrice: string; sku: string };

const emptyDraft = (): Draft => ({ name: "", description: "", unitPrice: "", sku: "" });

function formatMoney(n: number, currency = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
}

export function FinanceItemsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const res = await apiGet<ItemsResponse>("/api/finance/items?limit=100");
    if (!res.ok) {
      setError(res.error || "Could not load items.");
      setItems([]);
    } else {
      setItems(res.data?.items ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditId(null);
    setDraft(emptyDraft());
    setDialogOpen(true);
  };

  const openEdit = (item: ItemRow) => {
    if (!item.id) return;
    setEditId(item.id);
    setDraft({
      name: item.name || "",
      description: item.description || "",
      unitPrice: typeof item.unitPrice === "number" ? String(item.unitPrice) : "",
      sku: item.sku || "",
    });
    setDialogOpen(true);
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    const body = {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      unitPrice: draft.unitPrice ? Number(draft.unitPrice) : 0,
      sku: draft.sku.trim() || null,
    };
    const res = editId
      ? await apiPatch<ItemRow>(`/api/finance/items/${encodeURIComponent(editId)}`, body)
      : await apiPost<ItemRow>("/api/finance/items", body);
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setDialogOpen(false);
    setEditId(null);
    setDraft(emptyDraft());
    await load();
  };

  const onConfirmDelete = async () => {
    if (!deleteId) return;
    setError(null);
    const res = await apiDelete<{ ok?: boolean }>(`/api/finance/items/${encodeURIComponent(deleteId)}`);
    if (!res.ok) {
      setError(res.error || "Delete failed.");
      return;
    }
    setDeleteId(null);
    setDeleteOpen(false);
    await load();
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Items</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Products and services catalog for invoice line items.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add item
        </button>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading items…</p> : null}
      {error ? <FinanceErrorBanner message={error} /> : null}

      {!loading && !error ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">SKU</th>
                <th className="px-4 py-3 font-semibold">Price</th>
                <th className="px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    No catalog items yet.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id || item.name} className="border-b border-slate-100 dark:border-white/5">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      {item.id ? (
                        <Link
                          to={`/finance/items/${encodeURIComponent(item.id)}`}
                          className="text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                          {item.name?.trim() || item.id}
                        </Link>
                      ) : (
                        item.name?.trim() || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">
                      {item.sku?.trim() || "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-600 dark:text-slate-300">
                      {typeof item.unitPrice === "number"
                        ? formatMoney(item.unitPrice, item.currency || "USD")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {item.active === false ? "No" : "Yes"}
                    </td>
                    <td className="space-x-1 px-4 py-3 text-right">
                      {item.id ? (
                        <Link
                          to={`/finance/items/${encodeURIComponent(item.id)}`}
                          className="rounded-md px-2 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200"
                        >
                          Open
                        </Link>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="rounded-md px-2 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200"
                      >
                        Edit
                      </button>
                      {item.id ? (
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteId(item.id);
                            setDeleteOpen(true);
                          }}
                          className="rounded-md px-2 py-1 text-sm font-medium text-rose-700 hover:bg-rose-50 dark:text-rose-300"
                        >
                          Delete
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? "Edit item" : "New item"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Name</span>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Description</span>
              <input
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Unit price</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={draft.unitPrice}
                onChange={(e) => setDraft((d) => ({ ...d, unitPrice: e.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">SKU</span>
              <input
                value={draft.sku}
                onChange={(e) => setDraft((d) => ({ ...d, sku: e.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving || !draft.name.trim()}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : editId ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete item?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-400">This cannot be undone.</p>
          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteId(null);
              }}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onConfirmDelete()}
              className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-500"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
